"use client";

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiClient } from '../api/client';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../ui/components/Button';
import { TextField } from '../ui/components/TextField';
import { Table } from '../ui/components/Table';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { formatFloorPrice, formatVolume } from '../utils/formatters';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import {
  FeatherSearch,
  FeatherChevronDown,
  FeatherGrid,
  FeatherList,
  FeatherVerified,
  FeatherStar,
  FeatherUsers,
  FeatherExternalLink,
} from '@subframe/core';
import { VoteBoardTierBadge } from '../components/collection/VoteBoardTierBadge';
import { Checkbox } from '../ui/components/Checkbox';

interface Collection {
  id: string;
  slug?: string;
  name: string;
  description: string;
  image_url?: string;
  creator_address: string;
  is_verified: boolean;
  is_featured: boolean;
  is_top_community: boolean;
  total_supply: number;
  floor_price?: number;
  volume_24h: number;
  volume_7d: number;
  volume_30d: number;
  volume_all_time: number;
  xrpl_voteboard_score?: number | null;
  xrpl_voteboard_data?: {
    slug?: string;
    trustTier?: { tier: string; label: string; color: string } | null;
    currentPosition?: number | null;
    movement?: { direction?: string; positions?: number } | null;
  } | null;
  indexed_at?: string;
  created_at: string;
  // Mint status from associated launch
  mint_status?: 'minting_now' | 'minting_soon' | 'mint_upcoming' | null;
  launch_slug?: string | null;
}

type SortOption = 'volume' | 'volume_7d' | 'volume_30d' | 'floor' | 'name' | 'created' | 'trending' | 'voteboard';
type ViewMode = 'grid' | 'table';

const sortOptions: { value: SortOption; label: string; apiValue: string }[] = [
  { value: 'trending', label: 'Trending', apiValue: 'trending' },
  { value: 'volume', label: 'Volume (24h)', apiValue: 'volume' },
  { value: 'volume_7d', label: 'Volume (7d)', apiValue: 'volume' },
  { value: 'volume_30d', label: 'Volume (30d)', apiValue: 'volume' },
  { value: 'floor', label: 'Floor Price', apiValue: 'floor' },
  { value: 'name', label: 'Name (A-Z)', apiValue: 'name' },
  { value: 'created', label: 'Recently Added', apiValue: 'created' },
  { value: 'voteboard', label: 'Vote Board Rank', apiValue: 'voteboard' },
];

export const CollectionsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const initSortOption = (() => {
    const sort = searchParams.get('sort') as SortOption;
    return sort && sortOptions.some(opt => opt.value === sort) ? sort : 'trending';
  })();
  const initSearchQuery = searchParams.get('q') || '';
  const initViewMode = (searchParams.get('view') as ViewMode) || 'grid';
  const initTopCommunityOnly = searchParams.get('topCommunity') === 'true';
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<SortOption>(initSortOption);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initSearchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initSearchQuery);
  const [viewMode, setViewMode] = useState<ViewMode>(initViewMode);
  const [topCommunityOnly, setTopCommunityOnly] = useState(initTopCommunityOnly);
  const [trustTierFilter, setTrustTierFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update URL when filters change
  const updateURLParams = useCallback((params: {
    sort?: SortOption;
    q?: string;
    view?: ViewMode;
    topCommunity?: boolean;
  }) => {
    const newParams = new URLSearchParams(searchParams);

    if (params.sort !== undefined) {
      if (params.sort === 'trending') {
        newParams.delete('sort');
      } else {
        newParams.set('sort', params.sort);
      }
    }

    if (params.q !== undefined) {
      if (params.q) {
        newParams.set('q', params.q);
      } else {
        newParams.delete('q');
      }
    }

    if (params.view !== undefined) {
      if (params.view === 'grid') {
        newParams.delete('view');
      } else {
        newParams.set('view', params.view);
      }
    }

    if (params.topCommunity !== undefined) {
      if (params.topCommunity) {
        newParams.set('topCommunity', 'true');
      } else {
        newParams.delete('topCommunity');
      }
    }

    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const fetchCollections = useCallback(async (page: number, append: boolean = false) => {
    if (page === 1) {
      setLoading(true);
    }
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '24',
      });

      // Map sort option to API params
      const sortConfig = sortOptions.find(opt => opt.value === sortOption);
      if (sortConfig) {
        params.append('sortBy', sortConfig.apiValue);
        // For volume options, we sort by volume but the display shows different timeframes
        // For name, sort ascending; for others, sort descending
        params.append('sortOrder', sortOption === 'name' ? 'asc' : 'desc');
      }

      if (debouncedSearchQuery) {
        params.append('query', debouncedSearchQuery);
      }

      if (topCommunityOnly) {
        params.append('topCommunity', 'true');
      }

      const response = await apiClient.get<any>(`/collections?${params}`);

      if (response.success && response.data) {
        let newCollections = response.data.collections || [];

        // Always sort featured collections first
        newCollections = newCollections.sort((a: Collection, b: Collection) => {
          if (a.is_featured && !b.is_featured) return -1;
          if (!a.is_featured && b.is_featured) return 1;
          return 0;
        });

        if (append) {
          setCollections(prev => [...prev, ...newCollections]);
        } else {
          setCollections(newCollections);
        }

        if (response.data.pagination) {
          setTotalPages(response.data.pagination.pages || 1);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load collections');
    } finally {
      setLoading(false);
    }
  }, [sortOption, debouncedSearchQuery, topCommunityOnly]);

  // Fetch collections when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchCollections(1, false);
  }, [sortOption, debouncedSearchQuery, topCommunityOnly, fetchCollections]);

  // Handle sort change
  const handleSortChange = (newSort: SortOption) => {
    setSortOption(newSort);
    setShowSortDropdown(false);
    updateURLParams({ sort: newSort });
  };

  // Handle search
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    updateURLParams({ q: value });
  };

  // Handle view mode change
  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    updateURLParams({ view: mode });
  };

  // Handle top community filter change
  const handleTopCommunityChange = (checked: boolean) => {
    setTopCommunityOnly(checked);
    updateURLParams({ topCommunity: checked });
  };

  // Load more collections
  const loadMore = useCallback(() => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchCollections(nextPage, true);
    }
  }, [currentPage, totalPages, fetchCollections]);

  // Infinite scroll
  const sentinelRef = useInfiniteScroll({
    onLoadMore: loadMore,
    enabled: currentPage < totalPages && !loading && collections.length > 0,
  });

  // Filter collections by trust tier
  const filteredCollections = useMemo(() => {
    if (!trustTierFilter) return collections;
    return collections.filter(c => c.xrpl_voteboard_data?.trustTier?.tier === trustTierFilter);
  }, [collections, trustTierFilter]);

  const getCurrentSortLabel = (): string => {
    return sortOptions.find(opt => opt.value === sortOption)?.label || 'Trending';
  };

  // Render mint status badge
  const renderMintBadge = (collection: Collection, variant: 'grid' | 'table' = 'grid') => {
    if (!collection.mint_status) return null;

    const badgeConfig = {
      minting_now: {
        label: 'Minting Now',
        bgClass: 'bg-success-500',
        textClass: 'text-white',
      },
      minting_soon: {
        label: 'Minting Soon',
        bgClass: 'bg-brand-600',
        textClass: 'text-white',
      },
      mint_upcoming: {
        label: 'Mint Upcoming',
        bgClass: 'bg-neutral-600',
        textClass: 'text-white',
      },
    };

    const config = badgeConfig[collection.mint_status];
    if (!config) return null;

    if (variant === 'table') {
      return (
        <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${config.bgClass} ${config.textClass}`}>
          {config.label}
        </span>
      );
    }

    // Grid variant - positioned badge
    return (
      <div className={`absolute top-3 right-3 flex items-center gap-1 rounded-full ${config.bgClass} px-2 py-1 shadow-md`}>
        <span className={`text-xs font-semibold ${config.textClass}`}>{config.label}</span>
      </div>
    );
  };

  // Get volume based on sort option for display
  const getDisplayVolume = (collection: Collection): number => {
    switch (sortOption) {
      case 'volume_7d':
        return collection.volume_7d;
      case 'volume_30d':
        return collection.volume_30d;
      default:
        return collection.volume_24h;
    }
  };

  const getVolumeLabel = (): string => {
    switch (sortOption) {
      case 'volume_7d':
        return '7d Volume';
      case 'volume_30d':
        return '30d Volume';
      default:
        return '24h Volume';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSortDropdown) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSortDropdown]);

  return (
    <DefaultPageLayout>
      <div className="container max-w-none flex h-full w-full flex-col items-start gap-8 bg-default-background py-8 overflow-auto mobile:gap-4 mobile:py-4">
        {/* Page Header */}
        <div className="flex w-full flex-col items-start gap-2">
          <h1 className="text-heading-1 font-heading-1 text-default-font mobile:text-heading-2 mobile:font-heading-2">
            Collections
          </h1>
          <p className="text-body font-body text-subtext-color mobile:text-caption mobile:font-caption">
            Discover and explore NFT collections on the XRP Ledger
          </p>
        </div>

        {/* Trust Tier Legend — click to filter */}
        <div className="w-full flex items-center gap-4 flex-wrap text-caption font-caption text-subtext-color -my-3 mobile:-my-1">
          <span>
            Trust Tiers powered by{' '}
            <a href="https://nft.xrplvoteboard.com/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:text-brand-500 transition-colors">XRPL Vote Board</a>:
          </span>
          {([
            { tier: 'gold', icon: '\u2605', label: 'Gold', desc: 'High Community Trust', color: '#FFD700' },
            { tier: 'silver', icon: '\u25C6', label: 'Silver', desc: 'Strong Support', color: '#C0C0C0' },
            { tier: 'bronze', icon: '\u25B2', label: 'Bronze', desc: 'Community Recognized', color: '#CD7F32' },
          ] as const).map(({ tier, icon, label, desc, color }, i) => (
            <span key={tier} className="inline-flex items-center gap-1.5">
              <button
                onClick={() => setTrustTierFilter(prev => prev === tier ? null : tier)}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold transition-all cursor-pointer ${
                  trustTierFilter === tier ? 'ring-2 ring-offset-1 ring-offset-transparent' : 'opacity-80 hover:opacity-100'
                }`}
                style={{
                  borderColor: color,
                  color: color,
                  backgroundColor: trustTierFilter === tier ? `${color}40` : `${color}1A`,
                }}
              >
                {icon} {label}
              </button>
              <span className="text-subtext-color">({desc})</span>
            </span>
          ))}
        </div>

        {/* Controls Bar */}
        <div className="flex w-full items-center gap-3 mobile:flex-col mobile:items-stretch mobile:gap-2">
          {/* Search */}
          <TextField
            className="h-auto w-72 flex-none mobile:w-full"
            variant="filled"
            label=""
            helpText=""
            icon={<FeatherSearch />}
          >
            <TextField.Input
              placeholder="Search collections..."
              value={searchQuery}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleSearchChange(event.target.value);
              }}
            />
          </TextField>

          {/* Sort Dropdown */}
          <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="neutral-secondary"
              iconRight={<FeatherChevronDown />}
              onClick={() => setShowSortDropdown(!showSortDropdown)}
            >
              {getCurrentSortLabel()}
            </Button>

            {showSortDropdown && (
              <div className="absolute top-full left-0 mt-2 w-48 rounded-md border border-solid border-neutral-border bg-default-background shadow-lg z-20">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleSortChange(option.value)}
                    className={`w-full text-left px-4 py-2 text-body font-body hover:bg-neutral-100 transition-colors ${
                      sortOption === option.value ? 'bg-brand-100 text-brand-700' : 'text-default-font'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Top Community Filter */}
          <Checkbox
            label="Top Communities"
            checked={topCommunityOnly}
            onCheckedChange={handleTopCommunityChange}
          />

          {/* Spacer - pushes view toggle to the right */}
          <div className="flex-1 mobile:hidden" />

          {/* View Mode Toggle - hidden on mobile since table doesn't work well */}
          <div className="flex items-center rounded-md border border-solid border-neutral-border bg-neutral-50 mobile:hidden">
            <button
              onClick={() => handleViewModeChange('grid')}
              className={`h-10 px-3 rounded-l-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-brand-100 text-brand-700'
                  : 'text-subtext-color hover:bg-neutral-100'
              }`}
              title="Grid view"
            >
              <FeatherGrid className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleViewModeChange('table')}
              className={`h-10 px-3 rounded-r-md transition-colors ${
                viewMode === 'table'
                  ? 'bg-brand-100 text-brand-700'
                  : 'text-subtext-color hover:bg-neutral-100'
              }`}
              title="Table view"
            >
              <FeatherList className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Vote Board Banner - only shown when sorted by voteboard */}
        {sortOption === 'voteboard' && (
          <div className="w-full flex items-center justify-between gap-4 rounded-lg border border-brand-600 bg-brand-600/10 px-4 py-3">
            <div className="flex flex-col gap-1">
              <span className="text-body-bold font-body-bold text-default-font">
                Scores powered by XRPL Vote Board
              </span>
              <span className="text-caption font-caption text-subtext-color">
                Vote with XRP to rank your favorite collections. Unique and trusted voters boost scores.
              </span>
            </div>
            <a
              href="https://nft.xrplvoteboard.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-brand-600 text-white text-body-bold font-body-bold hover:bg-brand-700 transition-colors whitespace-nowrap"
            >
              Vote Now
              <FeatherExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Content */}
        {loading && currentPage === 1 ? (
          <div className="flex w-full items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <LoadingSpinner size="md" />
              <span className="text-body font-body text-subtext-color">Loading collections...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
            <div className="flex flex-col items-center gap-4">
              <span className="text-body font-body text-error">{error}</span>
              <Button variant="brand-secondary" onClick={() => fetchCollections(1, false)}>
                Try Again
              </Button>
            </div>
          </div>
        ) : collections.length === 0 ? (
          <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
            <div className="flex flex-col items-center gap-4">
              <span className="text-heading-3 font-heading-3 text-default-font">No collections found</span>
              <span className="text-body font-body text-subtext-color">
                {searchQuery
                  ? 'Try adjusting your search query'
                  : 'No collections are available yet'}
              </span>
              {searchQuery && (
                <Button variant="brand-secondary" onClick={() => setSearchQuery('')}>
                  Clear Search
                </Button>
              )}
            </div>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div className="w-full items-start gap-6 grid grid-cols-4 mobile:grid-cols-1 mobile:gap-4 tablet:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredCollections.map((collection, index) => (
              <Link
                key={collection.id}
                to={`/collections/${collection.slug || collection.id}`}
                className={`flex flex-col items-start overflow-hidden rounded-lg border border-solid ${
                  collection.is_featured
                    ? 'border-amber-400 ring-2 ring-amber-200'
                    : 'border-neutral-border hover:border-brand-300'
                } bg-default-background shadow-sm hover:shadow-md transition-all group`}
              >
                {/* Collection Image */}
                <div className="relative w-full aspect-square bg-neutral-100 overflow-hidden">
                  {collection.image_url ? (
                    <img
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      src={collection.image_url}
                      alt={collection.name}
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-6xl font-bold text-neutral-300">
                        {collection.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  {/* Featured Badge */}
                  {collection.is_featured && (
                    <div className="absolute top-3 left-3 flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-amber-400 px-2 py-1 shadow-md">
                      <FeatherStar className="h-3 w-3 text-white fill-white" />
                      <span className="text-xs font-semibold text-white">Featured</span>
                    </div>
                  )}
                  {/* Top Community Badge */}
                  {collection.is_top_community && (
                    <div className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-gradient-to-r from-violet-600 to-purple-500 px-2 py-1 shadow-md">
                      <FeatherUsers className="h-3 w-3 text-white" />
                      <span className="text-xs font-semibold text-white">Top Community</span>
                    </div>
                  )}
                  {/* Mint Status Badge (top-right) - takes priority over verified */}
                  {collection.mint_status ? (
                    renderMintBadge(collection, 'grid')
                  ) : collection.is_verified && (
                    /* Verified Badge - only shown if no mint status */
                    <div className="absolute top-3 right-3 p-1.5 rounded-full bg-white shadow-sm">
                      <FeatherVerified className="w-4 h-4 text-brand-700" />
                    </div>
                  )}
                </div>

                {/* Collection Info */}
                <div className="flex w-full flex-col gap-3 p-4">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-body-bold font-body-bold text-default-font truncate min-w-0">
                      {collection.name}
                    </span>
                    {collection.xrpl_voteboard_data?.trustTier && (
                      <span className="flex-shrink-0">
                        <VoteBoardTierBadge
                          tier={collection.xrpl_voteboard_data.trustTier.tier}
                          label={collection.xrpl_voteboard_data.trustTier.label}
                          color={collection.xrpl_voteboard_data.trustTier.color}
                        />
                      </span>
                    )}
                  </div>

                  {/* Stats Grid */}
                  <div className="flex flex-col gap-1">
                    <div className="flex justify-between items-center">
                      <span className="text-caption font-caption text-subtext-color">Items</span>
                      <span className="text-caption-bold font-caption-bold text-default-font">
                        {collection.total_supply.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-caption font-caption text-subtext-color">Floor</span>
                      <span className="text-caption-bold font-caption-bold text-default-font">
                        {formatFloorPrice(collection.floor_price)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-caption font-caption text-subtext-color">{getVolumeLabel()}</span>
                      <span className="text-caption-bold font-caption-bold text-default-font">
                        {formatVolume(getDisplayVolume(collection))}
                      </span>
                    </div>
                    {/* Vote Board Rank - always shown for consistent card height */}
                    <div className="flex justify-between items-center">
                      <span className="text-caption font-caption text-subtext-color">Vote Board Rank</span>
                      <span className="text-caption-bold font-caption-bold text-default-font">
                        {collection.xrpl_voteboard_data?.currentPosition != null ? (
                          <>
                            #{collection.xrpl_voteboard_data.currentPosition}
                            {collection.xrpl_voteboard_data.movement?.direction === 'up' && (
                              <span className="ml-1 text-success-700">{'\u2191'}{collection.xrpl_voteboard_data.movement.positions}</span>
                            )}
                            {collection.xrpl_voteboard_data.movement?.direction === 'down' && (
                              <span className="ml-1 text-error-700">{'\u2193'}{collection.xrpl_voteboard_data.movement.positions}</span>
                            )}
                          </>
                        ) : (
                          <span className="text-subtext-color">N/A</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          /* Table View */
          <div className="w-full overflow-x-auto">
            <Table
              header={
                <Table.HeaderRow>
                  <Table.HeaderCell>#</Table.HeaderCell>
                  <Table.HeaderCell>Collection</Table.HeaderCell>
                  <Table.HeaderCell>Items</Table.HeaderCell>
                  <Table.HeaderCell>Floor Price</Table.HeaderCell>
                  <Table.HeaderCell>24h Volume</Table.HeaderCell>
                  <Table.HeaderCell>7d Volume</Table.HeaderCell>
                  <Table.HeaderCell>All Time Volume</Table.HeaderCell>
                </Table.HeaderRow>
              }
            >
              {filteredCollections.map((collection, index) => (
                <Table.Row key={collection.id} clickable>
                  <Table.Cell>
                    <span className="text-body font-body text-subtext-color">
                      {(currentPage - 1) * 24 + index + 1}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <Link
                      to={`/collections/${collection.slug || collection.id}`}
                      className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                    >
                      {collection.image_url ? (
                        <img
                          src={collection.image_url}
                          alt={collection.name}
                          className="w-10 h-10 rounded-md object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-md bg-neutral-100 flex items-center justify-center">
                          <span className="text-body-bold font-body-bold text-neutral-400">
                            {collection.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="text-body-bold font-body-bold text-default-font">
                            {collection.name}
                          </span>
                          {collection.is_verified && (
                            <FeatherVerified className="w-4 h-4 text-brand-700" />
                          )}
                          {collection.is_top_community && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700">
                              <FeatherUsers className="w-3 h-3" />
                              <span className="text-xs font-medium">Top</span>
                            </span>
                          )}
                          {renderMintBadge(collection, 'table')}
                          {collection.xrpl_voteboard_data?.trustTier && (
                            <VoteBoardTierBadge
                              tier={collection.xrpl_voteboard_data.trustTier.tier}
                              label={collection.xrpl_voteboard_data.trustTier.label}
                              color={collection.xrpl_voteboard_data.trustTier.color}
                            />
                          )}
                        </div>
                      </div>
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-default-font">
                      {collection.total_supply.toLocaleString()}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body-bold font-body-bold text-brand-700">
                      {formatFloorPrice(collection.floor_price)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-default-font">
                      {formatVolume(collection.volume_24h)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-default-font">
                      {formatVolume(collection.volume_7d)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-body font-body text-default-font">
                      {formatVolume(collection.volume_all_time)}
                    </span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table>
          </div>
        )}

        {/* Infinite scroll sentinel */}
        {currentPage < totalPages && collections.length > 0 && (
          <div ref={sentinelRef} className="h-4 w-full" />
        )}

        {/* Loading indicator for infinite scroll */}
        {loading && currentPage > 1 && (
          <div className="flex w-full items-center justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        )}
      </div>
    </DefaultPageLayout>
  );
};
